<script setup lang="ts">
import { onBeforeMount, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useTemplateRefsList } from '@vueuse/core';
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import type { ApplicantId } from '@/types';
import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import { getFullName } from '@/helpers/format';
import api from '@/api';
import type { ExpenseTypeDto } from '@/api/backend/public/expense-types/expense-type.dto';
import { useHouseholdApplicants } from '@/helpers/useHouseholdApplicants';
import { useHouseholdMessages } from '@/helpers/useHouseholdMessages';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack } = useLoanAppProcess();

const isReady = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);
const isSecondaryDeclaration = false;
const isIncomeExpenseRequest = false;

const {
  primaryApplicant,
  primaryPartner,
  otherApplicants,
  authorisedUserId,
  applicantsMarried,
  householdValidApplicants,
} = useHouseholdApplicants(loanApp, authorisedUser, { isSecondaryDeclaration, isIncomeExpenseRequest });

const { expensesMessage } = useHouseholdMessages(
  { primaryPartner, otherApplicants, authorisedUserId },
  { isSecondaryDeclaration, isIncomeExpenseRequest },
);

const financials = ref(cloneDeep(loanApp.value.financials));
const expenseRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();
const activeTab = ref<ApplicantId | undefined>(
  householdValidApplicants.value.length === 1 ? householdValidApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id].expenses?.numberOfDependants !== undefined;

const validateExpenses = async () => {
  // Don't validate if married and secondary declaration
  if (isSecondaryDeclaration && applicantsMarried) return true;

  let isValid = true;
  const refCount = expenseRefs.value.length;
  for (let i = 0; i < refCount; i++) {
    isValid = (await expenseRefs.value[i].update()) ?? false;
    if (!isValid) {
      activeTab.value = householdValidApplicants.value[i];
      window.scrollTo(0, 0);
      break;
    }
  }
  return isValid;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const expenseTypesResponse = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    if (expenseTypesResponse.expenseTypes?.length) {
      expenseTypesResponse.expenseTypes.sort((a, b) => {
        const sortOrderA = a.sortOrder || 0;
        const sortOrderB = b.sortOrder || 0;
        return sortOrderA - sortOrderB;
      });
      expenseTypes.value = expenseTypesResponse.expenseTypes;
    }
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What are your household
        <BxHighlight>expenses</BxHighlight>?
      </h2>
    </div>
    <div v-if="loanApp.applicants.length > 1 && loanApp.actionType === 'createNew'" class="my-6">
      <p>{{ expensesMessage }}</p>
    </div>

    <div class="my-6 grid gap-4">
      <div v-for="applicantId in householdValidApplicants" :key="applicantId">
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <ExpensesForm
            v-if="isIncomeExpenseRequest && applicantsMarried && primaryApplicant"
            :ref="expenseRefs.set"
            v-model="financials[primaryApplicant.id].expenses"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :effective-expenses="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
          <ExpensesForm
            v-else
            :ref="expenseRefs.set"
            v-model="financials[applicantId].expenses"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="householdValidApplicants.length > 1"
            :disabled="isSecondaryDeclaration && applicantsMarried"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>
        <BxCard :class="`${applicantId === activeTab && 'hidden'} expense-card`">
          <div class="flex items-center">
            <div class="card-details">
              <div class="font-medium">
                <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400"
                  >({{ isCompleted(applicantId) ? 'completed' : 'incomplete' }})</span
                >
              </div>
            </div>
            <div class="card-button">
              <BxButton
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                {{ isCompleted(applicantId) ? 'Edit' : 'Add' }} expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
        <BxCard :class="`${applicantId === activeTab && 'hidden'} expense-card-mobile`">
          <div class="items-left flex flex-col">
            <div class="card-details">
              <div class="font-medium">
                <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
                <span class="ml-6 font-normal text-gray-400"
                  >({{ isCompleted(applicantId) ? 'completed' : 'incomplete' }})</span
                >
              </div>
            </div>
          </div>
          <div class="card-button">
            <BxButton
              variant="secondary"
              :disabled="activeTab && activeTab !== applicantId"
              @click="activeTab = applicantId"
            >
              {{ isCompleted(applicantId) ? 'Edit' : 'Add' }} expenses
            </BxButton>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.expense-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.expense-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-details {
  flex-grow: 1;
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
